import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
    Alert,
    Box,
    Button,
    Divider,
    FormControlLabel,
    Grid,
    MenuItem,
    Switch,
    Typography,
} from '@mui/material';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloudIcon from '@mui/icons-material/Cloud';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SaveIcon from '@mui/icons-material/Save';
import { Title, useNotify } from 'react-admin';
import { ExpansionPanel, InputField } from '@/components/common';
import {
    OcrConfigBO,
    OcrConfigService,
    OcrConfigVO,
} from '@/services/manageService/ocrConfigService';

const PROVIDER_OPTIONS = [
    { value: 'tess4j', label: 'Tess4J (Local)' },
    { value: 'ai', label: 'AI Integration' },
    { value: 'cloud', label: 'Cloud OCR' },
    { value: 'paddle', label: 'PaddleOCR (Reserved)' },
];

const CLOUD_VENDORS = [
    { value: 'aliyun', label: 'Alibaba Cloud' },
    { value: 'tencent', label: 'Tencent Cloud' },
    { value: 'azure', label: 'Azure' },
];

const defaultConfig = (): OcrConfigBO => ({
    provider: 'tess4j',
    tess4j: { lang: 'eng+chi_sim+chi_tra+accounting' },
    ai: { enabled: false, apiUrl: '', apiKey: '', model: '' },
    cloud: { enabled: false, vendor: '', accessKey: '', secretKey: '', endpoint: '' },
    paddle: { enabled: false, serviceUrl: '' },
});

function PanelTitle({
    icon,
    label,
    trailing,
}: {
    icon: ReactNode;
    label: string;
    trailing?: ReactNode;
}) {
    return (
        <Box display="flex" alignItems="center" gap={1} width="100%">
            {icon}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                {label}
            </Typography>
            {trailing ? (
                <Box onClick={e => e.stopPropagation()} onFocus={e => e.stopPropagation()}>
                    {trailing}
                </Box>
            ) : null}
        </Box>
    );
}

function EnableSwitch({
    enabled,
    onChange,
}: {
    enabled: boolean;
    onChange: (checked: boolean) => void;
}) {
    return (
        <FormControlLabel
            control={<Switch size="small" checked={enabled} onChange={e => onChange(e.target.checked)} />}
            label={enabled ? 'Enabled' : 'Disabled'}
            sx={{ m: 0 }}
        />
    );
}

export default function OcrConfig() {
    const notify = useNotify();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState<OcrConfigBO>(defaultConfig());

    const loadConfig = useCallback(async () => {
        setLoading(true);
        try {
            const res = await OcrConfigService.getConfig();
            const data = (res as { data?: OcrConfigVO })?.data ?? res;
            setConfig({
                provider: data.provider || 'tess4j',
                tess4j: { ...defaultConfig().tess4j, ...data.tess4j },
                ai: { ...defaultConfig().ai, ...data.ai },
                cloud: { ...defaultConfig().cloud, ...data.cloud },
                paddle: { ...defaultConfig().paddle, ...data.paddle },
            });
        } catch (e: any) {
            notify(e?.message || 'Failed to load OCR configuration', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await OcrConfigService.updateConfig(config);
            notify('OCR configuration saved', { type: 'success' });
            await loadConfig();
        } catch (e: any) {
            notify(e?.message || 'Failed to save configuration', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const updateAi = (patch: Partial<NonNullable<OcrConfigBO['ai']>>) => {
        setConfig(prev => ({
            ...prev,
            ai: { ...prev.ai, ...patch },
            provider: patch.enabled === true ? 'ai' : prev.provider,
        }));
    };

    const updateCloud = (patch: Partial<NonNullable<OcrConfigBO['cloud']>>) => {
        setConfig(prev => ({
            ...prev,
            cloud: { ...prev.cloud, ...patch },
            provider: patch.enabled === true ? 'cloud' : prev.provider,
        }));
    };

    if (loading) {
        return (
            <Box p={2}>
                <Title title="OCR Config" />
                <Typography color="text.secondary">Loading...</Typography>
            </Box>
        );
    }

    return (
        <Box p={2}>
            <Title title="OCR Config" />
            <Box display="flex" alignItems="center" gap={1} mb={2}>
                <DocumentScannerIcon color="primary" />
                <Typography variant="h5">OCR Engine Configuration</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
                Configure the OCR recognition engine. AI integration and Cloud OCR can be toggled on or off;
                when disabled, recognition falls back to the local Tess4J engine.
            </Typography>

            <Box sx={{ border: 1, borderColor: 'divider', mb: 3 }}>
                <ExpansionPanel
                    title={<PanelTitle icon={<DocumentScannerIcon fontSize="small" color="primary" />} label="Current Engine" />}
                    defaultExpanded
                    titleVariant="subtitle2"
                >
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <InputField
                                select
                                fullWidth
                                size="small"
                                label="Provider"
                                value={config.provider}
                                onChange={e => setConfig(prev => ({ ...prev, provider: e.target.value }))}
                            >
                                {PROVIDER_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </InputField>
                        </Grid>
                        <Grid size={12}>
                            <InputField
                                fullWidth
                                size="small"
                                label="Language (lang)"
                                helperText="Default: eng+chi_sim+chi_tra+accounting. Training data is packaged with the backend, so no path configuration is required."
                                value={config.tess4j?.lang || ''}
                                onChange={e => setConfig(prev => ({
                                    ...prev,
                                    tess4j: { ...prev.tess4j, lang: e.target.value },
                                }))}
                            />
                        </Grid>
                    </Grid>
                </ExpansionPanel>

                <ExpansionPanel
                    title={
                        <PanelTitle
                            icon={<SmartToyIcon fontSize="small" color="primary" />}
                            label="AI Integration"
                            trailing={
                                <EnableSwitch
                                    enabled={!!config.ai?.enabled}
                                    onChange={checked => updateAi({ enabled: checked })}
                                />
                            }
                        />
                    }
                    defaultExpanded
                >
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <InputField
                                fullWidth
                                size="small"
                                label="API URL"
                                value={config.ai?.apiUrl || ''}
                                onChange={e => updateAi({ apiUrl: e.target.value })}
                                display={!config.ai?.enabled}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <InputField
                                fullWidth
                                size="small"
                                label="Model Name"
                                value={config.ai?.model || ''}
                                onChange={e => updateAi({ model: e.target.value })}
                                display={!config.ai?.enabled}
                            />
                        </Grid>
                        <Grid size={12}>
                            <InputField
                                fullWidth
                                size="small"
                                type="password"
                                label="API Key"
                                value={config.ai?.apiKey || ''}
                                onChange={e => updateAi({ apiKey: e.target.value })}
                                display={!config.ai?.enabled}
                                helperText="Leave blank or use a **** prefix to keep the existing key unchanged"
                            />
                        </Grid>
                    </Grid>
                </ExpansionPanel>

                <ExpansionPanel
                    title={
                        <PanelTitle
                            icon={<CloudIcon fontSize="small" color="primary" />}
                            label="Cloud OCR"
                            trailing={
                                <EnableSwitch
                                    enabled={!!config.cloud?.enabled}
                                    onChange={checked => updateCloud({ enabled: checked })}
                                />
                            }
                        />
                    }
                    defaultExpanded
                >
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <InputField
                                select
                                fullWidth
                                size="small"
                                label="Vendor"
                                value={config.cloud?.vendor || ''}
                                onChange={e => updateCloud({ vendor: e.target.value })}
                                display={!config.cloud?.enabled}
                            >
                                {CLOUD_VENDORS.map(v => (
                                    <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
                                ))}
                            </InputField>
                        </Grid>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <InputField
                                fullWidth
                                size="small"
                                label="Endpoint"
                                value={config.cloud?.endpoint || ''}
                                onChange={e => updateCloud({ endpoint: e.target.value })}
                                display={!config.cloud?.enabled}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <InputField
                                fullWidth
                                size="small"
                                label="Access Key"
                                value={config.cloud?.accessKey || ''}
                                onChange={e => updateCloud({ accessKey: e.target.value })}
                                display={!config.cloud?.enabled}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <InputField
                                fullWidth
                                size="small"
                                type="password"
                                label="Secret Key"
                                value={config.cloud?.secretKey || ''}
                                onChange={e => updateCloud({ secretKey: e.target.value })}
                                display={!config.cloud?.enabled}
                            />
                        </Grid>
                    </Grid>
                </ExpansionPanel>

                <ExpansionPanel
                    title={
                        <PanelTitle
                            icon={<HourglassEmptyIcon fontSize="small" />}
                            label="PaddleOCR — Coming Soon"
                        />
                    }
                    defaultExpanded={false}
                    containerSx={{ opacity: 0.6 }}
                >
                    <Alert severity="info" sx={{ mb: 2 }}>
                        PaddleOCR is reserved for a future release and will be available after a standalone Python service is deployed.
                    </Alert>
                    <InputField
                        fullWidth
                        size="small"
                        label="Service URL (Reserved)"
                        value={config.paddle?.serviceUrl || ''}
                        display
                    />
                </ExpansionPanel>
            </Box>

            <Divider sx={{ mb: 2 }} />
            <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
            >
                {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
        </Box>
    );
}
